/**
 * Settings → Storage API client (Feature B3, 2026-05-22).
 *
 * Wraps Subagent A's endpoints (`settings-storage.ts`). The server is
 * already deployed on prod as of 2026-05-22; this client adheres to
 * the exact response shape the gateway returns.
 *
 * Endpoint contract:
 *
 *   GET /api/settings/storage/summary
 *     → {
 *         totalBytes: number,
 *         cachedAt: string,
 *         cacheTtlSec: number,
 *         byCategory: Array<{
 *           category: string,    // 'workspaces' | 'voice_inputs' | 'archives' | 'orphan_volumes' | 'bootstraps' | 'agent_config' | 'sessions' | 'skills' | 'history'
 *           bytes: number,
 *           percentage: number,  // 0-100
 *           exists: boolean,
 *         }>,
 *         cached?: boolean,
 *       }
 *
 *   GET /api/settings/storage/voice-inputs?range_days=N
 *     → {
 *         rangeDays: number,                 // 0 = all-time
 *         groups: Array<{
 *           cardId: string,
 *           title: string | null,
 *           count: number,
 *           totalBytes: number,
 *           items: Array<{
 *             id: string,
 *             filename: string,
 *             sizeBytes: number,
 *             durationSec: number | null,
 *             transcriptStatus: 'pending' | 'transcribing' | 'done' | 'failed',
 *             createdAt: string | null,
 *           }>,
 *         }>,
 *       }
 *
 *   DELETE /api/settings/storage/voice-inputs/bulk
 *     body: { olderThanDays?: number; cardIds?: string[] }
 *     → { deleted: number; freedBytes?: number }
 */
import { apiJson } from './client';

export type StorageCategoryId =
  | 'workspaces'
  | 'voice_inputs'
  | 'archives'
  | 'orphan_volumes'
  | 'bootstraps'
  | 'agent_config'
  | 'sessions'
  | 'skills'
  | 'history'
  | string;

export interface StorageCategoryItem {
  category: StorageCategoryId;
  bytes: number;
  percentage: number;
  exists: boolean;
}

export interface StorageSummary {
  totalBytes: number;
  cachedAt: string;
  cacheTtlSec: number;
  byCategory: StorageCategoryItem[];
  cached?: boolean;
}

export type TranscriptStatus = 'pending' | 'transcribing' | 'done' | 'failed';

export interface StorageVoiceItem {
  id: string;
  filename: string;
  sizeBytes: number;
  durationSec: number | null;
  transcriptStatus: TranscriptStatus;
  createdAt: string | null;
}

export interface StorageVoiceGroup {
  cardId: string;
  title: string | null;
  count: number;
  totalBytes: number;
  items: StorageVoiceItem[];
}

export interface StorageVoiceListResponse {
  rangeDays: number;
  groups: StorageVoiceGroup[];
}

export interface BulkDeleteResponse {
  /** Recordings moved to Trash. Kept for the old field name; see `trashed`. */
  deleted: number;
  /**
   * Recordings moved to Trash by this call. Since 2026-07-26 (migration 0156)
   * the bulk endpoint no longer unlinks audio: it soft-deletes, and the
   * recordings show up in the Trash block above with their real size, where a
   * per-item or typed-phrase delete frees the bytes. `freedBytes` is therefore
   * always absent from this response — a recording that moved to Trash freed
   * nothing yet, and claiming otherwise is how a Storage page stops being
   * believed.
   */
  trashed?: number;
  recoverable?: boolean;
  freedBytes?: number;
}

/**
 * Pre-defined range filters. We pass an integer day count to the
 * backend; 0 = all-time. The "older than 90" filter is **client-side**
 * because the backend only accepts a lower-bound cutoff. We fetch with
 * range_days=0 and filter in the UI — acceptable as the page is admin-
 * only and the row count is capped on the server.
 */
export type StorageVoiceRange = 'all' | '7' | '30' | '90' | 'older-than-90';

export function rangeToQuery(range: StorageVoiceRange): string {
  switch (range) {
    case '7':
      return '?range_days=7';
    case '30':
      return '?range_days=30';
    case '90':
      return '?range_days=90';
    case 'older-than-90':
      // No native server-side filter — fetch everything, post-filter UI-side.
      return '?range_days=0';
    case 'all':
    default:
      return '?range_days=0';
  }
}

/**
 * GET /api/settings/storage/trash — everything in Trash with its REAL size.
 *
 * `bytes` is the honest size of that item's own tree. `nestedIn` is set when
 * an ancestor already in this list contains those bytes (a trashed card inside
 * an archived project, a file inside a trashed card's folder); such rows show
 * their size but contribute nothing to `totalBytes`, which is exactly what an
 * empty-the-Trash would free.
 */
/**
 * `voiceInput` joined 2026-07-26 (migration 0156). It is the only Trash entity
 * whose whole cost is one audio file, and until that day pressing Delete on the
 * Voice tab unlinked it instantly and irreversibly. Trashed NOTES are
 * deliberately not in this list: they have no bytes, and a byte-ranked table is
 * the wrong place for a 0 B row. They are listed in Admin -> Trash instead.
 */
export type TrashEntityType = 'org' | 'project' | 'board' | 'card' | 'file' | 'voiceInput';

export interface StorageTrashItem {
  type: TrashEntityType;
  id: string;
  name: string;
  orgId: string | null;
  orgName: string | null;
  deletedAt: string | null;
  bytes: number;
  nestedIn: string | null;
  containers: number;
  containerBytes: number;
  blockedReason: string | null;
}

export interface StorageTrashResponse {
  items: StorageTrashItem[];
  totalBytes: number;
  byType: Array<{ type: TrashEntityType; count: number; bytes: number }>;
  measuredMs: number;
  measuredAt: string;
}

export interface StorageTrashEmptyResponse {
  ok: true;
  cardsPurged: number;
  filesPurged: number;
  /** Optional: an older gateway mid-rolling-deploy does not send these. */
  voiceInputsPurged?: number;
  notesPurged?: number;
  containersRemoved: number;
  bytesReclaimed: number;
  skipped: Array<{ id: string; title: string | null; reason: string }>;
}

/** The server refuses anything else. Typing it is the point — see the route. */
export const TRASH_EMPTY_CONFIRMATION = 'DELETE EVERYTHING';

/**
 * GET /api/settings/storage/account-trash — the bin a DELETED account's files
 * go into (migration 0218) instead of being erased on the spot.
 *
 * `unavailable` IS THE FIELD THAT MATTERS HERE. The gateway sets it when
 * chaos.storage_trash could not be READ, and then every number below is a zero
 * that means "not measured" — not "the bin is empty". The two look identical on
 * the wire and completely different to a person deciding whether a deleted
 * account's files are still sitting on this disk. Any renderer of this type
 * must branch on it before it prints a single figure.
 */
export interface AccountTrashRow {
  id: string;
  accountId: string;
  /** Where the files lived before deletion — what this entry actually is. */
  originKey: string;
  trashKey: string;
  entryKind: string;
  domain: string | null;
  bytes: number;
  files: number;
  trashedAt: string;
  expiresAt: string;
  daysLeft: number;
  restoredAt: string | null;
  purgedAt: string | null;
  /** Set when erasure re-checked the path afterwards and found nothing there.
   *  `false` means erasure ran and the check did NOT confirm absence. */
  purgeVerifiedAbsent: boolean | null;
  receiptId: string | null;
}

export interface AccountTrashTotals {
  pendingEntries: number;
  pendingBytes: number;
  pendingFiles: number;
  expiredEntries: number;
  expiredBytes: number;
  purgedEntries: number;
  purgedBytes: number;
  restoredEntries: number;
}

export interface AccountTrashAccount {
  accountId: string;
  entries: number;
  bytes: number;
  nextExpiry: string | null;
}

/**
 * Whether anything is recording, sweeping and measuring at all. Zero entries
 * with a dormant registry is silence; zero entries with a live one is an empty
 * bin. Optional because a gateway mid-rolling-deploy may not send it yet.
 */
export interface AccountTrashRegistry {
  dormant: boolean;
  dormantReason: string | null;
  lastError: string | null;
  recorded: number;
  deduped: number;
  recordFailed: number;
  recordLastError: string | null;
  /** null = the sweeper did not answer. Not the same as "awake". */
  sweepDormant: boolean | null;
  sweepDormantReason: string | null;
  sweepLastTickAt: string | null;
  sweepLastError: string | null;
}

export interface AccountTrashResponse {
  rows: AccountTrashRow[];
  totals: AccountTrashTotals;
  byAccount: AccountTrashAccount[];
  unavailable: boolean;
  unavailableReason: string | null;
  registry?: AccountTrashRegistry;
}

export const settingsStorageApi = {
  summary(): Promise<StorageSummary> {
    return apiJson<StorageSummary>(`/settings/storage/summary`);
  },

  voiceInputs(range: StorageVoiceRange): Promise<StorageVoiceListResponse> {
    return apiJson<StorageVoiceListResponse>(
      `/settings/storage/voice-inputs${rangeToQuery(range)}`,
    );
  },

  /**
   * Bulk-delete voice inputs that match a server-side filter. We pass
   * `cardIds` to delete every voice row attached to the chosen cards,
   * or `olderThanDays` to garbage-collect by age. The two filters
   * combine with AND on the server.
   */
  bulkDelete(filter: {
    olderThanDays?: number;
    cardIds?: string[];
  }): Promise<BulkDeleteResponse> {
    return apiJson<BulkDeleteResponse>(`/settings/storage/voice-inputs/bulk`, {
      method: 'DELETE',
      json: filter,
    });
  },

  /**
   * Not cached anywhere: this is the number the founder decides on, and a
   * stale one is worse than a slow one.
   */
  trash(): Promise<StorageTrashResponse> {
    return apiJson<StorageTrashResponse>(`/settings/storage/trash`);
  },

  /**
   * Empty the Trash. Cards + knowledge files only — orgs, projects and boards
   * keep their own guarded deletes, so one button can never destroy an org.
   */
  emptyTrash(): Promise<StorageTrashEmptyResponse> {
    return apiJson<StorageTrashEmptyResponse>(`/settings/storage/trash/all`, {
      method: 'DELETE',
      json: { confirm: TRASH_EMPTY_CONFIRMATION },
    });
  },

  /**
   * Files belonging to deleted accounts. Read-only — there is no purge sibling
   * on this client either, because erasure is a CLI act behind `--commit`.
   * `all` adds restored and purged entries, which are the audit trail.
   */
  accountTrash(opts?: { all?: boolean; limit?: number }): Promise<AccountTrashResponse> {
    const qs = new URLSearchParams();
    if (opts?.all) qs.set('all', '1');
    if (opts?.limit != null) qs.set('limit', String(opts.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiJson<AccountTrashResponse>(`/settings/storage/account-trash${suffix}`);
  },
};

export const settingsStorageKeys = {
  all: ['settings-storage'] as const,
  summary: () => [...settingsStorageKeys.all, 'summary'] as const,
  trash: () => [...settingsStorageKeys.all, 'trash'] as const,
  accountTrash: (all: boolean) =>
    [...settingsStorageKeys.all, 'account-trash', all] as const,
  voice: (range: StorageVoiceRange) =>
    [...settingsStorageKeys.all, 'voice', range] as const,
};

/**
 * Friendly label for a category bucket. The wire format uses snake_case
 * machine names; the UI wants human strings.
 */
export function labelForCategory(id: StorageCategoryId): string {
  switch (id) {
    case 'workspaces':
      return 'Card files';
    case 'voice_inputs':
      return 'Voice inputs';
    case 'archives':
      return 'Archived files';
    case 'orphan_volumes':
      return 'Orphan volumes';
    case 'bootstraps':
      return 'Bootstraps';
    case 'agent_config':
      return 'Agent config';
    case 'sessions':
      return 'Sessions';
    case 'skills':
      return 'Skills';
    case 'history':
      return 'History';
    default:
      return id;
  }
}
